import React from "react";
import { Languages } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * LanguagesStrip — Compact brutalist band promoting the 5-language support.
 * Big USP for teams with foreign workers (Estonian / Swedish / Russian speakers).
 */
export const LanguagesStrip = () => {
  const { t, setLang, lang: currentLang } = useLanguage();

  const LANGS = [
    { code: "fi", flag: "🇫🇮", label: t("languages.fi") },
    { code: "ee", flag: "🇪🇪", label: t("languages.ee") },
    { code: "se", flag: "🇸🇪", label: t("languages.se") },
    { code: "en", flag: "🇬🇧", label: t("languages.en") },
    { code: "ru", flag: "🇷🇺", label: t("languages.ru") },
  ];

  return (
    <section
      id="kielet"
      data-testid="languages-strip"
      className="relative py-16 md:py-20 bg-black border-y-2 border-[#1f1f1f] overflow-hidden"
    >
      {/* Diagonal hazard slash background */}
      <div
        aria-hidden="true"
        className="absolute -left-4 top-0 bottom-0 w-3 hazard-stripes-anim opacity-70"
      />
      <div
        aria-hidden="true"
        className="absolute -right-4 top-0 bottom-0 w-3 hazard-stripes-anim opacity-70"
      />

      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Left copy block */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 border-2 border-[#FFEA00] bg-black px-3 py-1.5 mb-6">
              <Languages className="w-3.5 h-3.5 text-[#FFEA00]" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFEA00]">
                {t("languages_strip.eyebrow")}
              </span>
            </div>
            <h2 className="font-display font-bold text-white text-3xl md:text-4xl lg:text-5xl leading-[0.95] tracking-tight uppercase">
              {t("languages_strip.headline_1")}{" "}
              <span className="text-[#FFEA00]">{t("languages_strip.headline_accent")}</span>{" "}
              {t("languages_strip.headline_2")}
            </h2>
            <p className="mt-5 font-mono text-sm md:text-[15px] text-[#A3A3A3] leading-relaxed max-w-md">
              {t("languages_strip.subline")}
            </p>
          </div>

          {/* Right — 5 language chips */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {LANGS.map((l) => {
                const isActive = currentLang === l.code;
                return (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    data-testid={`lang-chip-${l.code}`}
                    className={`group relative flex flex-col items-center gap-2 px-3 py-5 border-2 transition-all ${
                      isActive
                        ? "border-[#FFEA00] bg-[#FFEA00]/[0.08]"
                        : "border-[#2a2a2a] bg-[#0a0a0a] hover:border-[#FFEA00]/60 hover:bg-[#FFEA00]/[0.03]"
                    }`}
                  >
                    <span className="text-4xl leading-none" aria-hidden="true">
                      {l.flag}
                    </span>
                    <span
                      className={`font-display font-bold text-sm tracking-widest uppercase ${
                        isActive ? "text-[#FFEA00]" : "text-white group-hover:text-[#FFEA00]"
                      } transition-colors`}
                    >
                      {l.code.toUpperCase()}
                    </span>
                    <span className="font-mono text-[10px] text-[#A3A3A3] tracking-wide uppercase">
                      {l.label}
                    </span>
                    {isActive && (
                      <span
                        aria-hidden="true"
                        className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#FFEA00]"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
